import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Stack,
  Icon,
  Button,
  SimpleGrid,
  useToast,
  HStack,
  Image,
  Link,
  Spinner,
  VStack,
  Tag,
  Divider,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiTruck,
  FiExternalLink,
  FiCreditCard,
  FiClock,
  FiArrowLeft,
  FiPackage,
  FiMapPin,
  FiShield,
  FiFileText,
  FiShare2,
  FiCopy,
  FiUploadCloud,
  FiAlertTriangle,
  FiCamera,
  FiRotateCcw,
  FiFile,
} from "react-icons/fi";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import WithSubnavigation from "./navbar";
import { useCookies } from "react-cookie";
import MapPreviewInline from "./UploadPrescription/MapPreviewInline";

import { API_BASE } from "../../config";
import { resolveFileUrl } from "../../utils/fileUrl";
import { performLogout } from "../../utils/logout";

const hasCookie = (name) => {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .some((c) => c.trim().startsWith(`${name}=`));
};

const RAZORPAY_SCRIPT_ID = "razorpay-checkout-script";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existing = document.getElementById(RAZORPAY_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = RAZORPAY_SCRIPT_ID;
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const PrescriptionOrderSummary = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [cookies, , removeCookie] = useCookies(["token", "role"]);
  const isLoggedIn = Boolean(cookies.token || hasCookie("token"));

  const [username, setUsername] = useState("");
  const [fullUser, setFullUser] = useState(null);

  const initialOrder = location.state?.order || null;
  const initialMeds = location.state?.medicines || [];

  const [order, setOrder] = useState(initialOrder);
  const [meds, setMeds] = useState(initialMeds);
  const [loading, setLoading] = useState(!initialOrder);
  const [razorpayReady, setRazorpayReady] = useState(false);
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [paying, setPaying] = useState(false);
  const [acceptedItems, setAcceptedItems] = useState({});
  const [submittingReview, setSubmittingReview] = useState(false);
  const [autoPayAfterReview, setAutoPayAfterReview] = useState(false);
  const [justConfirmedReview, setJustConfirmedReview] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [prescriptionHistory, setPrescriptionHistory] = useState([]);
  const [prescriptionHistoryLoading, setPrescriptionHistoryLoading] = useState(false);
  const [uploadingPrescription, setUploadingPrescription] = useState(false);
  const [showPrescriptionHistory, setShowPrescriptionHistory] = useState(false);
  const prescriptionInputRef = React.useRef(null);

  // Pre-accept all proposed changes so the total reflects pharmacist's version by default.
  // Unavailable (out of stock) items are always removed and cannot be toggled by the user.
  useEffect(() => {
    if (!order?.proposedChanges?.length) return;
    setAcceptedItems(prev => {
      const next = { ...prev };
      let changed = false;
      for (const change of order.proposedChanges) {
        const mustRemove = change.changeType === 'unavailable';
        if (next[change.requestedItemKey] === undefined || mustRemove) {
          next[change.requestedItemKey] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [order?.proposedChanges]);

  useEffect(() => {
    const bootstrap = async () => {
      const [scriptOk] = await Promise.all([
        loadRazorpayScript(),
        axios
          .get(`${API_BASE}/order/config`, { withCredentials: true })
          .then((res) => setRazorpayKeyId(res.data?.razorpayKeyId || ""))
          .catch(() => {}),
      ]);
      setRazorpayReady(Boolean(scriptOk));
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchUser = async () => {
      try {
        const { data } = await axios.post(`${API_BASE}`, {}, { withCredentials: true });
        if (data?.status && data?.user) {
          setUsername(data.user);
          const fullUserData = await axios.get(`${API_BASE}/patient/myInfo/${data.user}`);
          setFullUser(fullUserData.data);
        }
      } catch {}
    };
    fetchUser();
  }, [isLoggedIn]);

  useEffect(() => {
    if (!orderId) return;
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/order/${orderId}`, {
          withCredentials: true,
        });
        setOrder(res.data || null);
        setMeds(res.data?.items || []);
      } catch (err) {
        toast({
          title: "Could not load order",
          description: "Please check your connection and try again.",
          status: "error",
          duration: 2200,
          isClosable: true,
          position: "bottom-right",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId, toast]);

  const status = order?.status || "Processing";
  const pharmacyName = order?.pharmacy?.name || "Not set";
  const deliveryAddress =
    order?.deliveryLocation?.address || order?.address || "Not set";
  const deliveryCoords = order?.deliveryLocation
    ? [order.deliveryLocation.lng, order.deliveryLocation.lat]
    : null;
  const pharmacyCoords = order?.pharmacy?.coords || null;
  const renderMeds = meds?.length ? meds : order?.items || [];
  const files = order?.prescriptionFiles || [];
  const deliveryWorkflow = order?.deliveryWorkflow || {};
  const selectedDeliveryOption = deliveryWorkflow?.selectedOption || null;
  const deliveryFee =
    selectedDeliveryOption?.amount ??
    deliveryWorkflow?.quote?.amount ??
    selectedDeliveryOption?.deliveryFee ??
    deliveryWorkflow?.quote?.deliveryFee ??
    0;
  const deliveryEta = selectedDeliveryOption?.etaLabel || "";
  const trackingUrl = deliveryWorkflow?.trackingUrl || "";
  const courierName = deliveryWorkflow?.courierName || "";
  const courierPhone = deliveryWorkflow?.courierPhone || "";
  const borzoStatusRaw = deliveryWorkflow?.borzoStatus || deliveryWorkflow?.status || "";
  const borzoStatusLabel = borzoStatusRaw
    ? borzoStatusRaw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "";

  const medicinesSubtotal = Number(order?.bill || 0);
  const payableTotal = Number(
    order?.paymentWorkflow?.payableAmount ??
      (medicinesSubtotal + Number(deliveryFee || 0))
  );

  // Live medicines total reflecting current accept/reject toggle state or pharmacist fulfillment
  const liveTotal = useMemo(() => {
    const changes = order?.proposedChanges || [];
    const fulfillmentLines = order?.pharmacistFulfillment?.lines || [];
    
    // If no changes and no fulfillment, return the stored bill
    if (!changes.length && !fulfillmentLines.length) return medicinesSubtotal;
    
    // Build fulfillment price map by item name/key
    const fulfillmentPriceMap = {};
    fulfillmentLines.forEach(line => {
      const price = line.allocations?.length 
        ? Number(line.allocations[0].saleRate || 0) 
        : Number(line.mrpOverride || 0);
      if (line.itemName) fulfillmentPriceMap[line.itemName] = price;
      if (line.requestedItemKey) fulfillmentPriceMap[line.requestedItemKey] = price;
    });
    
    let total = 0;
    for (let i = 0; i < (order?.items || []).length; i++) {
      const item = order.items[i];
      // Match the key format used in pharmacistOrders: "itemCode-index" or "name-index"
      const itemKey = item.itemCode ? `${item.itemCode}-${i}` : `${item.name || 'item'}-${i}`;
      const altItemKey = item.itemCode || String(item.productId || item.name || '');
      
      // Check if there's a proposed change (match by index-based key or name)
      const change = changes.find(
        c => c.requestedItemKey === itemKey || c.requestedItemKey === altItemKey || c.requestedItemName === item.name
      );
      
      // Check if there's a fulfillment price (lookup by both keys and name)
      const fulfillmentPrice = fulfillmentPriceMap[item.name] || fulfillmentPriceMap[itemKey] || fulfillmentPriceMap[altItemKey] || 0;
      const catalogPrice = Number(item.price || 0);
      
      if (!change) {
        // No change - use fulfillment price if available, else catalog price
        const price = fulfillmentPrice > 0 ? fulfillmentPrice : catalogPrice;
        total += price * Number(item.quantity || 1);
      } else {
        // Use the change's key for accepted lookup, fallback to generated key
        const lookupKey = change.requestedItemKey || itemKey;
        const accepted = Boolean(acceptedItems[lookupKey] ?? true);
        if (change.changeType === 'unavailable') {
          // Out-of-stock items are always removed; user cannot keep them
          // adds 0
        } else if (change.changeType === 'price_change') {
          // Always use the newPrice from the change (it's the pharmacist-confirmed price)
          const price = Number(change.newPrice || 0) > 0 ? Number(change.newPrice) : (fulfillmentPrice > 0 ? fulfillmentPrice : catalogPrice);
          total += accepted ? price * Number(item.quantity || 1) : catalogPrice * Number(item.quantity || 1);
        } else if (change.changeType === 'substitute') {
          total += accepted
            ? Number(change.substitutePrice || fulfillmentPrice || 0) * Number(item.quantity || 1)
            : catalogPrice * Number(item.quantity || 1);
        } else if (change.changeType === 'quantity_limit') {
          total += accepted
            ? catalogPrice * Number(change.newQuantity || item.quantity || 1)
            : catalogPrice * Number(item.quantity || 1);
        } else {
          total += (fulfillmentPrice > 0 ? fulfillmentPrice : catalogPrice) * Number(item.quantity || 1);
        }
      }
    }
    // Apply overall (gross) discount from pharmacist
    const grossDiscountPercent = Number(order?.pharmacistFulfillment?.grossDiscountPercent || 0);
    if (grossDiscountPercent > 0) {
      total = total * (1 - grossDiscountPercent / 100);
    }
    return Number(total.toFixed(2));
  }, [order?.items, order?.proposedChanges, order?.pharmacistFulfillment, acceptedItems]);

  // Gross discount info for display
  const grossDiscountPercent = Number(order?.pharmacistFulfillment?.grossDiscountPercent || 0);
  const paymentDone = Boolean(order?.paymentWorkflow?.isFinalized);
  const workflowStage = order?.workflowStage || "";
  const isPendingPharmacistReview = workflowStage === "review";
  const isPendingUserReview = workflowStage === "pending_user_review";
  const isPendingPayment = workflowStage === "pending_payment" || workflowStage === "payment_pending";
  const isRejected = workflowStage === "rejected";
  const isCancelled = workflowStage === "cancelled" || order?.status === "Cancelled";
  const requiresPayment = !paymentDone && isPendingPayment && payableTotal > 0 && !isRejected && !isCancelled;
  const pendingPricingSync = !paymentDone && payableTotal <= 0 && !isRejected && !isCancelled;

  const pageTitle = isRejected
    ? "Order rejected"
    : isCancelled
    ? "Order cancelled"
    : paymentDone
    ? "Order placed"
    : isPendingUserReview
    ? "Review changes"
    : isPendingPayment
    ? "Confirm and pay"
    : isPendingPharmacistReview
    ? "Order sent"
    : "Review your order";
  const pageSubtitle = isRejected
    ? "The pharmacy could not fulfill this order. You may place a new order with different items or pharmacy."
    : isCancelled
    ? "This order has been cancelled. You may place a new order if needed."
    : paymentDone
    ? "Your pharmacy, medicines, and delivery are locked. Track the order or review the details below."
    : isPendingUserReview
    ? "The pharmacy has proposed some changes. Review each item and confirm your selections."
    : isPendingPayment
    ? "The pharmacy has confirmed your order. Complete payment to proceed."
    : isPendingPharmacistReview
    ? "Your order has been sent to the pharmacy. The pharmacist is reviewing and will notify you shortly."
    : "Your order details are ready.";

  const paymentStatusTone = isRejected
    ? { bg: "#fee2e2", color: "#dc2626", label: "Rejected" }
    : isCancelled
    ? { bg: "#fee2e2", color: "#dc2626", label: "Cancelled" }
    : paymentDone
    ? { bg: "#dcfce7", color: "#166534", label: "Paid" }
    : isPendingUserReview
    ? { bg: "#fef9c3", color: "#854d0e", label: "Review needed" }
    : isPendingPayment
    ? { bg: "#eff6ff", color: "#1d4ed8", label: "Processing payment" }
    : { bg: "#fff7ed", color: "#c2410c", label: "Pending" };

  const orderStageTone = isRejected
    ? { bg: "#fee2e2", color: "#dc2626", label: "Order Rejected" }
    : isCancelled
    ? { bg: "#fee2e2", color: "#dc2626", label: "Order Cancelled" }
    : paymentDone
    ? { bg: "#ecfdf5", color: "#166534", label: "Ready to fulfill" }
    : isPendingUserReview
    ? { bg: "#fef9c3", color: "#854d0e", label: "Awaiting your review" }
    : workflowStage === "payment_pending" || workflowStage === "pending_payment"
    ? { bg: "#eff6ff", color: "#1d4ed8", label: "Processing payment" }
    : { bg: "#f8fafc", color: "#475569", label: status };

  const hasRxMedicines = renderMeds.some(
    m => m.prescription_required === true || m.prescription_required === "TRUE" || m.prescription_required === "Prescription Required" || m.isPrescriptionRequired
  );
  const missingPrescription = hasRxMedicines && (order?.prescriptionFiles || []).length === 0;

  const fetchPrescriptionHistory = async () => {
    if (prescriptionHistory.length) { setShowPrescriptionHistory(true); return; }
    setPrescriptionHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/order/prescription/history`, { withCredentials: true });
      setPrescriptionHistory(Array.isArray(data) ? data : []);
      setShowPrescriptionHistory(true);
    } catch (e) {
      setPrescriptionHistory([]);
    } finally {
      setPrescriptionHistoryLoading(false);
    }
  };

  const handlePrescriptionUpload = async (fileList) => {
    if (!fileList?.length || !order?._id) return;
    const accepted = Array.from(fileList).filter(f =>
      ["image/jpeg", "image/png", "application/pdf"].includes(f.type)
    );
    if (!accepted.length) {
      toast({ title: "Only JPG, PNG or PDF allowed", status: "warning", duration: 2200, isClosable: true, position: "bottom-right" });
      return;
    }
    setUploadingPrescription(true);
    try {
      const form = new FormData();
      accepted.forEach(f => form.append("prescriptions", f));
      const { data } = await axios.post(`${API_BASE}/order/${order._id}/prescription`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOrder(prev => ({ ...prev, prescriptionFiles: data.prescriptionFiles }));
      toast({ title: "Prescription uploaded", status: "success", duration: 1800, isClosable: true, position: "bottom-right" });
    } catch (e) {
      toast({ title: "Upload failed", description: e?.response?.data?.message || "Please try again.", status: "error", duration: 2600, isClosable: true, position: "bottom-right" });
    } finally {
      setUploadingPrescription(false);
    }
  };

  const handleUseSavedPrescription = async (savedFile) => {
    if (!order?._id) return;
    setUploadingPrescription(true);
    try {
      const { data } = await axios.post(`${API_BASE}/order/${order._id}/prescription`, {
        existingFilePath: savedFile.path,
        existingFileName: savedFile.fileName,
        existingOriginalName: savedFile.originalName,
        existingMimeType: savedFile.mimeType,
      }, { withCredentials: true });
      setOrder(prev => ({ ...prev, prescriptionFiles: data.prescriptionFiles }));
      setShowPrescriptionHistory(false);
      toast({ title: "Prescription attached", status: "success", duration: 1800, isClosable: true, position: "bottom-right" });
    } catch (e) {
      toast({ title: "Could not attach prescription", status: "error", duration: 2600, isClosable: true, position: "bottom-right" });
    } finally {
      setUploadingPrescription(false);
    }
  };

  const getFileUrl = (file) => {
    return resolveFileUrl(file?.path) || null;
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: "My medicine order — Local Pharma",
        text: `Order from ${pharmacyName} · ${renderMeds.length} medicines · ₹${payableTotal.toFixed(0)}`,
        url,
      }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).then(() => {
        toast({ title: "Order link copied", status: "success", duration: 1800, isClosable: true, position: "bottom-right" });
      }).catch(() => {
        toast({ title: url, status: "info", duration: 4000, isClosable: true, position: "bottom-right" });
      });
    }
  };

  const handleLogout = () => performLogout(removeCookie);

  const handlePayNow = async () => {
    if (!order?._id) return;
    if (!window.Razorpay || !razorpayReady) {
      toast({
        title: "Razorpay is not ready",
        description: "Reload the page and try again.",
        status: "error",
        duration: 2400,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    try {
      setPaying(true);
      const { data } = await axios.post(
        `${API_BASE}/order/${order._id}/razorpay/order`,
        {},
        { withCredentials: true }
      );

      const razorpay = new window.Razorpay({
        key: data?.keyId || razorpayKeyId,
        amount: data?.amount,
        currency: data?.currency || "INR",
        name: "PILLSTACK",
        description: `Prescription order ${order._id}`,
        order_id: data?.orderId,
        prefill: {
          name: data?.patient?.name || "",
          email: data?.patient?.email || "",
          contact: data?.patient?.contact || "",
        },
        theme: { color: "#0f766e" },
        handler: async (response) => {
          try {
            const verify = await axios.post(
              `${API_BASE}/order/${order._id}/razorpay/verify`,
              response,
              { withCredentials: true }
            );
            toast({
              title: "Payment confirmed",
              description: "Your order is confirmed. The pharmacy will prepare and dispatch it shortly.",
              status: "success",
              duration: 2600,
              isClosable: true,
              position: "bottom-right",
            });
            navigate("/order/confirmed", {
              replace: true,
              state: { order: verify.data?.order || order },
            });
          } catch (error) {
            toast({
              title: "Payment verification failed",
              description:
                error?.response?.data?.message ||
                "The payment was made but could not be verified yet.",
              status: "error",
              duration: 3200,
              isClosable: true,
              position: "bottom-right",
            });
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });

      razorpay.open();
    } catch (error) {
      setPaying(false);
      toast({
        title: "Could not start payment",
        description:
          error?.response?.data?.message || "Please try again in a moment.",
        status: "error",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    }
  };

  // Auto-trigger payment after review confirmation when flag is set
  useEffect(() => {
    if (autoPayAfterReview && isPendingPayment && !paymentDone && !paying && razorpayReady) {
      setAutoPayAfterReview(false);
      setJustConfirmedReview(false);
      handlePayNow();
    }
  }, [autoPayAfterReview, isPendingPayment, paymentDone, paying, razorpayReady, handlePayNow]);

  // Clear justConfirmedReview when payment is done or user navigates away from review
  useEffect(() => {
    if (paymentDone || (!isPendingUserReview && !isPendingPayment)) {
      setJustConfirmedReview(false);
    }
  }, [paymentDone, isPendingUserReview, isPendingPayment]);

  const handleAcceptChanges = async () => {
    if (!order?._id) return;
    setSubmittingReview(true);
    try {
      const proposedChanges = order?.proposedChanges || [];
      const acceptedItemsPayload = proposedChanges.map((change) => ({
        itemKey: change.requestedItemKey,
        accepted: change.changeType === 'unavailable'
          ? true
          : Boolean(acceptedItems[change.requestedItemKey] ?? false),
      }));
      const { data } = await axios.post(
        `${API_BASE}/order/${order._id}/user-accept-changes`,
        { acceptedItems: acceptedItemsPayload },
        { withCredentials: true }
      );
      setOrder((prev) => ({
        ...prev,
        workflowStage: data.workflowStage,
        bill: data.bill,
        items: data.items ?? prev.items,
        proposedChanges: data.proposedChanges,
      }));
      toast({
        title: "Changes confirmed",
        description: "Your selections are saved. Opening payment...",
        status: "success",
        duration: 1500,
        isClosable: true,
        position: "bottom-right",
      });
      // Set flag to auto-trigger payment after state updates and keep review panel visible
      setAutoPayAfterReview(true);
      setJustConfirmedReview(true);
    } catch (error) {
      toast({
        title: "Could not submit review",
        description: error?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!order?._id) return;
    const confirmed = window.confirm("Are you sure you want to cancel this order?");
    if (!confirmed) return;
    setCancelling(true);
    try {
      await axios.post(
        `${API_BASE}/order/cancel-order`,
        { orderId: order._id },
        { withCredentials: true }
      );
      toast({
        title: "Order cancelled",
        status: "info",
        duration: 2000,
        isClosable: true,
        position: "bottom-right",
      });
      setOrder((prev) => ({ ...prev, status: "Cancelled" }));
      navigate("/");
    } catch (error) {
      toast({
        title: "Could not cancel order",
        description: error?.response?.data?.message || error?.response?.data || "Please try again.",
        status: "error",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setCancelling(false);
    }
  };

  const summaryItems = useMemo(
    () => [
      { label: "Prescription", value: files.length ? `${files.length} uploaded` : "Optional", color: files.length ? "#166534" : "#4338ca" },
      { label: "Medicines", value: `${renderMeds.length} added`, color: "#0f172a" },
      { label: "Location", value: deliveryAddress === "Not set" ? "Pending" : "Ready", color: deliveryAddress === "Not set" ? "#92400e" : "#166534" },
      { label: "Pharmacy", value: pharmacyName, color: pharmacyName === "Not set" ? "#64748b" : "#0f172a" },
    ],
    [renderMeds.length, files.length, deliveryAddress, pharmacyName]
  );

  return (
    <Box bg="#f8fafc" minH="100vh">
      <WithSubnavigation
        username={isLoggedIn ? username : ""}
        name={isLoggedIn ? fullUser?.Name : ""}
        isLoggedIn={isLoggedIn}
        sidebarOffset="0px"
        onLogout={handleLogout}
      />
      {/* Simple top bar */}
      <Box
        bg="white"
        borderBottom="1px solid #e2e8f0"
        px={{ base: 4, md: 8 }}
        py={3}
        position="sticky"
        top="74px"
        zIndex={99}
      >
        <Flex align="center" justify="space-between">
          <Button
            leftIcon={<FiArrowLeft size={14} />}
            variant="ghost"
            size="sm"
            fontWeight="700"
            color="#64748b"
            borderRadius="10px"
            _hover={{ bg: "#f1f5f9", color: "#0f172a" }}
            onClick={() => navigate(-1)}
            pl={0}
          >
            Back
          </Button>
          <HStack spacing={3}>
            {(orderId || order?._id) && (
              <Text fontSize="13px" fontWeight="700" color="#94a3b8" m={0}>
                Order #{(orderId || order._id).slice(-8).toUpperCase()}
              </Text>
            )}
            <Box
              as="span"
              fontSize="11px"
              fontWeight="700"
              px={2.5}
              py="3px"
              borderRadius="full"
              color={isRejected ? "#dc2626" : isCancelled ? "#dc2626" : paymentDone ? "#065f46" : isPendingUserReview ? "#92400e" : isPendingPayment ? "#1e40af" : "#64748b"}
              bg={isRejected ? "#fee2e2" : isCancelled ? "#fee2e2" : paymentDone ? "#d1fae5" : isPendingUserReview ? "#fef9c3" : isPendingPayment ? "#eff6ff" : "#f1f5f9"}
              border={`1px solid ${isRejected ? "#fecaca" : isCancelled ? "#fecaca" : paymentDone ? "#6ee7b7" : isPendingUserReview ? "#fde68a" : isPendingPayment ? "#bfdbfe" : "#e2e8f0"}`}
            >
              {isRejected ? "Rejected" : isCancelled ? "Cancelled" : paymentDone ? "Paid" : isPendingUserReview ? "Review needed" : isPendingPayment ? "Payment due" : isPendingPharmacistReview ? "Under review" : "Pending"}
            </Box>
          </HStack>
        </Flex>
      </Box>

      {/* ── Body ── */}
      <Box px={{ base: 4, md: 8, xl: 12 }} pt={5} pb={10} maxW="1480px" mx="auto">
          <Box
            display={{ base: "flex", xl: "grid" }}
            flexDirection={{ base: "column" }}
            gridTemplateColumns={{ xl: "1fr 360px" }}
            gap={5}
            alignItems="start"
          >

            {/* ── Left col: order contents ── */}
            <Stack spacing={4}>

              {/* Pharmacist reviewing banner */}
              {isPendingPharmacistReview && (
                <Box
                  bg="white"
                  border="1px solid #fde68a"
                  borderRadius="20px"
                  p={5}
                  boxShadow="0 2px 12px rgba(234,179,8,0.08)"
                >
                  <HStack spacing={3} mb={3}>
                    <Box w="8px" h="8px" borderRadius="full" bg="#eab308" flexShrink={0} />
                    <Text fontWeight="800" color="#0f172a" fontSize="md" m={0}>Pharmacist is reviewing your order</Text>
                  </HStack>
                  <Text fontSize="sm" color="#64748b" m={0}>
                    The pharmacist is checking stock and prices. You'll be able to pay once they confirm availability — usually within a few minutes.
                  </Text>
                </Box>
              )}

              {/* Rejected order banner */}
              {isRejected && (
                <Box
                  bg="#fef2f2"
                  border="1px solid #fecaca"
                  borderRadius="20px"
                  p={5}
                  boxShadow="0 2px 12px rgba(220,38,38,0.08)"
                >
                  <HStack spacing={3} mb={3}>
                    <Box w="8px" h="8px" borderRadius="full" bg="#dc2626" flexShrink={0} />
                    <Text fontWeight="800" color="#0f172a" fontSize="md" m={0}>Order rejected by pharmacy</Text>
                  </HStack>
                  <Text fontSize="sm" color="#64748b" m={0}>
                    {order?.rejectionReason || "The pharmacy could not fulfill this order. Please place a new order with different medicines or try a different pharmacy."}
                  </Text>
                </Box>
              )}

              {/* User review panel — show whenever pharmacist has proposed changes */}
              {(isPendingUserReview || justConfirmedReview || order?.proposedChanges?.length > 0) && (
                <Box
                  bg="white"
                  border="1px solid #fde68a"
                  borderRadius="20px"
                  overflow="hidden"
                  boxShadow="0 2px 12px rgba(234,179,8,0.08)"
                >
                  <Flex align="center" justify="space-between" px={5} pt={5} pb={4} borderBottom="1px solid #fef9c3">
                    <Box>
                      <Text fontWeight="800" color="#0f172a" fontSize="md" m={0} letterSpacing="-0.02em">
                        {order.pharmacy?.name || "Pharmacy"} updated your order
                      </Text>
                      <Text fontSize="xs" color="#64748b" m={0} mt="2px">
                        {paymentDone 
                          ? "Order confirmed and paid" 
                          : isPendingUserReview 
                            ? "Review all medicines and confirm to proceed" 
                            : "Your order is ready for payment"}
                      </Text>
                    </Box>
                    <Box 
                      as="span" 
                      fontSize="11px" 
                      fontWeight="700" 
                      color={paymentDone ? "#166534" : isPendingUserReview ? "#854d0e" : "#1e40af"} 
                      bg={paymentDone ? "#dcfce7" : isPendingUserReview ? "#fef9c3" : "#eff6ff"} 
                      px={3} 
                      py="5px" 
                      borderRadius="full" 
                      border={`1px solid ${paymentDone ? "#86efac" : isPendingUserReview ? "#fde68a" : "#bfdbfe"}`}
                    >
                      {paymentDone ? "Confirmed" : isPendingUserReview ? "Needs review" : "Payment due"}
                    </Box>
                  </Flex>
                  {(() => {
                    const fulfillmentMap = {};
                    (order?.pharmacistFulfillment?.lines || []).forEach(l => { fulfillmentMap[l.itemName] = l; });
                    // Also index fulfillment lines by item index (from requestedItemKey "code-idx")
                    const fulfillmentIndexMap = {};
                    (order?.pharmacistFulfillment?.lines || []).forEach(l => {
                      const parts = (l.requestedItemKey || '').split('-');
                      const n = parseInt(parts[parts.length - 1], 10);
                      if (!isNaN(n)) fulfillmentIndexMap[n] = l;
                    });
                    const changesMap = {};
                    (order?.proposedChanges || []).forEach(c => { changesMap[c.requestedItemName] = c; });
                    // Also index changes by item index
                    const changesIndexMap = {};
                    (order?.proposedChanges || []).forEach(c => {
                      const parts = (c.requestedItemKey || '').split('-');
                      const n = parseInt(parts[parts.length - 1], 10);
                      if (!isNaN(n)) changesIndexMap[n] = c;
                    });
                    const unavailableItems = (order?.proposedChanges || []).filter(c => c.changeType === 'unavailable');
                    const unavailableNames = new Set(unavailableItems.map(c => c.requestedItemName));
                    // Extract 0-based index from requestedItemKey (format: "itemCode-idx" or "name-idx")
                    const unavailableIndexes = new Set(
                      unavailableItems.map(c => {
                        const parts = (c.requestedItemKey || '').split('-');
                        const last = parts[parts.length - 1];
                        const n = parseInt(last, 10);
                        return isNaN(n) ? -1 : n;
                      }).filter(n => n >= 0)
                    );
                    const availableMeds = renderMeds.filter((m, i) => {
                      const name = m.product_name || m.Name || m.name || '';
                      return !unavailableNames.has(name) && !unavailableIndexes.has(i);
                    });
                    // availableMeds is a filtered subset — track original indexes
                    const availableMedsWithOrigIndex = renderMeds
                      .map((m, i) => ({ m, origIdx: i }))
                      .filter(({ m, origIdx }) => {
                        const name = m.product_name || m.Name || m.name || '';
                        return !unavailableNames.has(name) && !unavailableIndexes.has(origIdx);
                      });
                    return (
                      <Stack spacing={0} divider={<Box h="1px" bg="#fef9c3" />}>
                        {/* Unchanged medicines — read-only rows */}
                        {availableMedsWithOrigIndex.map(({ m, origIdx }, idx) => {
                          const itemName = m.product_name || m.Name || m.name || '';
                          // Look up change and fulfillment by original index first (handles name mismatch)
                          const change = changesIndexMap[origIdx] || changesMap[itemName];
                          const fl = fulfillmentIndexMap[origIdx] || fulfillmentMap[itemName];
                          // Prefer the pharmacist's explicit price change; fall back to fulfillment batch rate only if no override was set
                          const fulfillmentSaleRate = fl?.allocations?.length ? Number(fl.allocations[0].saleRate || 0) : 0;
                          const catalogPrice = Number(m.price || 0);
                          const newPrice = (change?.changeType === 'price_change' && change?.newPrice > 0)
                            ? Number(change.newPrice)
                            : (fulfillmentSaleRate > 0 ? fulfillmentSaleRate : null);
                          const finalPrice = newPrice ?? catalogPrice;
                          const originalPrice = (Math.abs(finalPrice - catalogPrice) > 0.01) ? catalogPrice
                            : (change?.changeType === 'price_change' ? Number(change.requestedPrice || 0) : null);
                          const discountPercent = Number(fl?.discountPercent || 0);
                          const hasOriginal = originalPrice > 0 && Math.abs(originalPrice - finalPrice) > 0.01;
                          const priceChanged = hasOriginal || discountPercent > 0;
                          if (priceChanged) {
                            const isPriceReduced = hasOriginal && finalPrice < originalPrice;
                            const priceColor = isPriceReduced ? "#059669" : "#d97706";
                            const priceBadgeBg = isPriceReduced ? "#d1fae5" : "#fff7ed";
                            const badgeLabel = discountPercent > 0 ? `${discountPercent}% off` : isPriceReduced ? "Price reduced" : "Price changed";
                            return (
                              <Flex key={idx} align="center" justify="space-between" px={5} py={4} gap={3}>
                                <Box flex={1} minW={0}>
                                  <HStack spacing={2} mb={1} flexWrap="wrap">
                                    <Text fontWeight="700" color="#0f172a" fontSize="sm" m={0}>{itemName}</Text>
                                    <Box as="span" fontSize="10px" fontWeight="700" color={priceColor} bg={priceBadgeBg} px={2} py="2px" borderRadius="full">{badgeLabel}</Box>
                                  </HStack>
                                  {(m.composition || m.key_ingredients) && (
                                    <Text fontSize="xs" color="#94a3b8" m={0} mb={1}>{m.composition || m.key_ingredients}</Text>
                                  )}
                                  <HStack spacing={2} mt={1}>
                                    {hasOriginal && <Text fontSize="sm" color="#94a3b8" textDecoration="line-through" m={0}>₹{originalPrice.toFixed(2)}</Text>}
                                    <Text fontSize="sm" fontWeight="700" color={priceColor} m={0}>₹{finalPrice.toFixed(2)}</Text>
                                    {hasOriginal && (
                                      <Text fontSize="xs" color={priceColor} m={0}>
                                        {isPriceReduced ? `-₹${(originalPrice - finalPrice).toFixed(2)}` : `+₹${(finalPrice - originalPrice).toFixed(2)}`}
                                      </Text>
                                    )}
                                  </HStack>
                                  {change?.reason && <Text fontSize="xs" color="#94a3b8" m={0} mt={1} fontStyle="italic">{change.reason}</Text>}
                                </Box>
                                <HStack spacing={3} flexShrink={0}>
                                  <Text fontSize="12px" color="#64748b" m={0}>×{m.qty || m.quantity || 1}</Text>
                                  <Text fontSize="sm" fontWeight="700" color={priceColor} m={0}>₹{finalPrice.toFixed(2)}</Text>
                                </HStack>
                              </Flex>
                            );
                          }
                          // No price change — simple read-only row
                          return (
                            <Flex key={idx} align="center" justify="space-between" px={5} py={3} gap={3}>
                              <Box minW={0} flex={1}>
                                <Text fontWeight="600" color="#0f172a" fontSize="sm" m={0} noOfLines={1}>{itemName}</Text>
                                {(m.composition || m.key_ingredients) && (
                                  <Text fontSize="xs" color="#94a3b8" m={0} mt="1px">{m.composition || m.key_ingredients}</Text>
                                )}
                              </Box>
                              <HStack spacing={3} flexShrink={0}>
                                <Text fontSize="12px" color="#64748b" m={0}>×{m.qty || m.quantity || 1}</Text>
                                {finalPrice > 0 && <Text fontSize="sm" fontWeight="700" color="#0f172a" m={0}>₹{finalPrice.toFixed(2)}</Text>}
                              </HStack>
                            </Flex>
                          );
                        })}
                        {/* Unavailable medicines — toggle rows */}
                        {unavailableItems.map((change, idx) => {
                          const isAccepted = Boolean(acceptedItems[change.requestedItemKey] ?? true);
                          const srcItem = renderMeds.find(m => (m.product_name || m.Name || m.name) === change.requestedItemName);
                          return (
                            <Box key={`unavail-${idx}`} px={5} py={4}>
                              <Flex align="flex-start" justify="space-between" gap={3}>
                                <Box flex={1} minW={0}>
                                  <HStack spacing={2} mb={1}>
                                    <Text fontWeight="700" color="#0f172a" fontSize="sm" m={0}>{change.requestedItemName}</Text>
                                    <Box as="span" fontSize="10px" fontWeight="700" color="#dc2626" bg="#fff1f2" px={2} py="2px" borderRadius="full">Out of stock</Box>
                                  </HStack>
                                  {(srcItem?.composition || srcItem?.key_ingredients) && (
                                    <Text fontSize="xs" color="#94a3b8" m={0} mb={1}>{srcItem.composition || srcItem.key_ingredients}</Text>
                                  )}
                                  {change.reason && <Text fontSize="xs" color="#94a3b8" m={0} mt={1} fontStyle="italic">{change.reason}</Text>}
                                </Box>
                                <Box flexShrink={0} textAlign="right">
                                  <Text fontSize="10px" color="#94a3b8" m={0} mb={1} fontWeight="600">Removed</Text>
                                  <Box
                                    as="span"
                                    w="48px" h="26px" borderRadius="full"
                                    bg="#0f766e"
                                    position="relative" display="inline-block"
                                  >
                                    <Box position="absolute" top="3px" left="25px" w="20px" h="20px" borderRadius="full" bg="white" boxShadow="0 1px 4px rgba(0,0,0,0.2)" />
                                  </Box>
                                </Box>
                              </Flex>
                            </Box>
                          );
                        })}
                      </Stack>
                    );
                  })()}
                  {/* Running total + confirm/paid status */}
                  <Box px={5} py={4} bg={paymentDone ? "#f0fdf4" : "#f8fafc"} borderTop="1px solid #f1f5f9">
                    <Flex align="center" justify="space-between" mb={3}>
                      <Box>
                        <Text fontSize="sm" color="#64748b" m={0}>{paymentDone ? "Total paid" : "Updated total"}</Text>
                        {liveTotal !== medicinesSubtotal && !paymentDone && (
                          <Text fontSize="xs" color="#94a3b8" textDecoration="line-through" m={0}>
                            was ₹{medicinesSubtotal.toFixed(2)}
                          </Text>
                        )}
                      </Box>
                      <Text fontSize="lg" fontWeight="800" color={paymentDone ? "#166534" : "#0f172a"} m={0}>
                        ₹{liveTotal.toFixed(2)}
                      </Text>
                    </Flex>
                    {isCancelled ? (
                      <Box border="1px solid #fca5a5" borderRadius="12px" p={3} bg="#fef2f2">
                        <Flex align="center" gap={2}>
                          <Icon as={FiAlertTriangle} color="#dc2626" boxSize={5} />
                          <Text fontSize="sm" fontWeight="700" color="#dc2626" m={0}>{isRejected ? "Order rejected" : "Order cancelled"}</Text>
                        </Flex>
                      </Box>
                    ) : paymentDone ? (
                      <Box border="1px solid #bbf7d0" borderRadius="12px" p={3} bg="white">
                        <Flex align="center" gap={2}>
                          <Icon as={FiCheckCircle} color="#16a34a" boxSize={5} />
                          <Text fontSize="sm" fontWeight="700" color="#166534" m={0}>Payment completed</Text>
                        </Flex>
                      </Box>
                    ) : (
                      <>
                        <Button
                          colorScheme="teal"
                          size="md"
                          width="100%"
                          fontWeight="700"
                          borderRadius="12px"
                          isLoading={isPendingUserReview ? submittingReview : paying}
                          onClick={isPendingUserReview ? handleAcceptChanges : handlePayNow}
                        >
                          {isPendingUserReview ? "Confirm selections & proceed to payment" : "Proceed to payment"}
                        </Button>
                        <Button
                          variant="ghost"
                          colorScheme="red"
                          size="sm"
                          width="100%"
                          mt={2}
                          fontWeight="600"
                          borderRadius="12px"
                          isLoading={cancelling}
                          onClick={handleCancelOrder}
                        >
                          Cancel Order
                        </Button>
                      </>
                    )}
                  </Box>
                  {/* Delivery tracking — show when delivery has started */}
                  {(order?.deliveryWorkflow?.borzoOrderId || order?.deliveryWorkflow?.trackingUrl || order?.deliveryWorkflow?.status) && paymentDone && (
                    <Box px={5} py={4} bg="#eff6ff" borderTop="1px solid #bfdbfe">
                      <Flex align="center" gap={2} mb={2}>
                        <Icon as={FiTruck} color="#1d4ed8" boxSize={4} />
                        <Text fontSize="sm" fontWeight="700" color="#1e40af" m={0}>
                          Delivery {order?.deliveryWorkflow?.status || "in progress"}
                        </Text>
                      </Flex>
                      {order?.deliveryWorkflow?.trackingUrl && (
                        <Link 
                          href={order.deliveryWorkflow.trackingUrl} 
                          isExternal 
                          color="#2563eb" 
                          fontSize="sm"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          Track your order <Icon as={FiExternalLink} boxSize={3} />
                        </Link>
                      )}
                      {order?.deliveryWorkflow?.borzoOrderId && !order?.deliveryWorkflow?.trackingUrl && (
                        <Text fontSize="xs" color="#64748b" m={0}>
                          Order ID: {order.deliveryWorkflow.borzoOrderId}
                        </Text>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Medicines — only show when no pharmacist action taken */}
              {!isPendingUserReview && !justConfirmedReview && !order?.proposedChanges?.length && <Box
                bg="white"
                border="1px solid #e7edf3"
                borderRadius="20px"
                overflow="hidden"
                boxShadow="0 2px 12px rgba(15,23,42,0.06)"
              >
                {(() => {
                  const fulfillmentMap = {};
                  (order?.pharmacistFulfillment?.lines || []).forEach(line => {
                    fulfillmentMap[line.itemName] = line;
                  });
                  const changesMap = {};
                  (order?.proposedChanges || []).forEach(c => {
                    changesMap[c.requestedItemName] = c;
                  });
                  const unavailableItems = (order?.proposedChanges || []).filter(c => c.changeType === 'unavailable');
                  const unavailableNames = new Set(unavailableItems.map(c => c.requestedItemName));
                  const availableMeds = renderMeds.filter(m => {
                    const name = m.product_name || m.Name || m.name || '';
                    return !unavailableNames.has(name);
                  });
                  const totalCount = availableMeds.length + unavailableItems.length;
                  return (
                    <>
                      <Flex align="center" justify="space-between" px={5} pt={5} pb={4} borderBottom="1px solid #f1f5f9">
                        <Box>
                          <Text fontWeight="800" color="#0f172a" fontSize="md" m={0} letterSpacing="-0.02em">Medicines</Text>
                          <Text fontSize="xs" color="#64748b" m={0} mt="2px">{totalCount} item{totalCount !== 1 ? "s" : ""} in this order</Text>
                        </Box>
                        <Box as="span" fontSize="12px" fontWeight="700" color="#0f766e" bg="#f0fdfa" px={3} py="5px" borderRadius="full" border="1px solid #99f6e4">
                          {availableMeds.length} added
                        </Box>
                      </Flex>
                      <Box px={5} py={4}>
                        {loading ? (
                          <HStack spacing={2} color="#64748b" py={6} justify="center">
                            <Spinner size="sm" />
                            <Text m={0} fontSize="sm">Loading order…</Text>
                          </HStack>
                        ) : (totalCount > 0) ? (
                          <Stack spacing={2}>
                            {availableMeds.map((m, idx) => {
                              const itemName = m.product_name || m.Name || m.name || '';
                              const fl = fulfillmentMap[itemName];
                              const ch = changesMap[itemName];
                              const catalogPrice = Number(m.price || 0);
                              // Use pharmacist fulfillment price when available (saleRate from allocations)
                              const fulfillmentSaleRate = fl?.allocations?.length 
                                ? Number(fl.allocations[0].saleRate || 0) 
                                : 0;
                              const fulfillmentMrp = fl?.mrpOverride || 0;
                              // Effective price: use fulfillment saleRate, fallback to catalog price
                              const finalPrice = fulfillmentSaleRate > 0 ? fulfillmentSaleRate : catalogPrice;
                              const originalPrice = fulfillmentSaleRate > 0 && Math.abs(fulfillmentSaleRate - catalogPrice) > 0.01
                                ? catalogPrice 
                                : (ch?.changeType === 'price_change' ? Number(ch.requestedPrice || 0) : null);
                              const discountPercent = Number(fl?.discountPercent || 0);
                              const hasOriginal = originalPrice > 0 && Math.abs(originalPrice - finalPrice) > 0.01;
                              const hasDiscount = discountPercent > 0;
                              return (
                                <Flex
                                  key={m._id || m.id || m.product_id || m.productId || idx}
                                  align="flex-start"
                                  justify="space-between"
                                  border="1px solid #edf2f7"
                                  borderRadius="14px"
                                  px={4}
                                  py={3}
                                  bg="#fafafa"
                                  gap={3}
                                  _hover={{ borderColor: "#d0e4f0", bg: "#f4f8fb" }}
                                  transition="all 0.1s"
                                >
                                  <HStack spacing={3} minW={0} flex={1} align="flex-start">
                                    <Box w="7px" h="7px" borderRadius="full" flexShrink={0} mt="5px"
                                      bg={(m.prescription_required || m.isPrescriptionRequired) ? "#dc2626" : "#0f766e"}
                                    />
                                    <Box minW={0} flex={1}>
                                      <Text fontWeight="700" color="#0f172a" noOfLines={1} m={0} fontSize="sm">
                                        {itemName || "Medicine"}
                                      </Text>
                                      {(m.composition || m.key_ingredients) && (
                                        <Text fontSize="xs" color="#94a3b8" noOfLines={1} m={0} mt="1px">
                                          {m.composition || m.key_ingredients}
                                        </Text>
                                      )}
                                      {(hasOriginal || hasDiscount) && (
                                        <HStack spacing={1.5} mt={1} flexWrap="wrap">
                                          {hasOriginal && (
                                            <Text fontSize="11px" color="#94a3b8" textDecoration="line-through" m={0}>
                                              ₹{originalPrice.toFixed(2)}
                                            </Text>
                                          )}
                                          {hasDiscount && (
                                            <Box as="span" fontSize="10px" fontWeight="700" color="#059669" bg="#d1fae5" px={1.5} py="1px" borderRadius="full">
                                              -{discountPercent}% off
                                            </Box>
                                          )}
                                        </HStack>
                                      )}
                                    </Box>
                                  </HStack>
                                  <HStack spacing={2} flexShrink={0} align="flex-start">
                                    {(m.prescription_required || m.isPrescriptionRequired) && (
                                      <Box as="span" fontSize="10px" fontWeight="700" color="#b91c1c" bg="#fff1f2" px={2} py="2px" borderRadius="full">Rx</Box>
                                    )}
                                    <Box textAlign="right">
                                      <Box as="span" fontSize="12px" fontWeight="700" color="#334155" bg="#f1f5f9" px={2.5} py="3px" borderRadius="full">
                                        ×{m.qty || m.quantity || 1}
                                      </Box>
                                      {finalPrice > 0 && (
                                        <Text fontSize="12px" fontWeight="700" color="#0f766e" m={0} mt={1}>
                                          ₹{finalPrice.toFixed(2)}
                                        </Text>
                                      )}
                                    </Box>
                                  </HStack>
                                </Flex>
                              );
                            })}
                            {unavailableItems.map((c, idx) => {
                              const srcItem = renderMeds.find(m => (m.product_name || m.Name || m.name) === c.requestedItemName);
                              const composition = srcItem?.composition || srcItem?.key_ingredients || null;
                              return (
                                <Flex
                                  key={`unavail-${idx}`}
                                  align="center"
                                  justify="space-between"
                                  border="1px solid #fee2e2"
                                  borderRadius="14px"
                                  px={4}
                                  py={3}
                                  bg="#fff9f9"
                                  gap={3}
                                  opacity={0.8}
                                >
                                  <HStack spacing={3} minW={0} flex={1} align="flex-start">
                                    <Box w="7px" h="7px" borderRadius="full" flexShrink={0} mt="5px" bg="#dc2626" />
                                    <Box minW={0}>
                                      <HStack spacing={2} flexWrap="wrap">
                                        <Text fontWeight="700" color="#94a3b8" noOfLines={1} m={0} fontSize="sm" textDecoration="line-through">
                                          {c.requestedItemName}
                                        </Text>
                                        <Box as="span" fontSize="10px" fontWeight="700" color="#dc2626" bg="#fff1f2" px={2} py="1px" borderRadius="full" flexShrink={0}>
                                          Unavailable
                                        </Box>
                                      </HStack>
                                      {composition && (
                                        <Text fontSize="xs" color="#94a3b8" noOfLines={1} m={0} mt="1px">{composition}</Text>
                                      )}
                                    </Box>
                                  </HStack>
                                  <HStack spacing={2} flexShrink={0}>
                                    <Box as="span" fontSize="12px" fontWeight="700" color="#334155" bg="#f1f5f9" px={2.5} py="3px" borderRadius="full">
                                      ×{c.requestedQuantity || 1}
                                    </Box>
                                    <Text fontSize="12px" color="#94a3b8" textDecoration="line-through" m={0}>
                                      ₹{Number(c.requestedPrice || 0).toFixed(2)}
                                    </Text>
                                  </HStack>
                                </Flex>
                              );
                            })}
                          </Stack>
                        ) : (
                          <VStack py={8} spacing={2} color="#94a3b8">
                            <Icon as={FiPackage} boxSize={8} />
                            <Text fontSize="sm" m={0} fontWeight="600">No medicines captured</Text>
                          </VStack>
                        )}
                      </Box>
                    </>
                  );
                })()}
              </Box>}

              {/* Prescription + Map side by side */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {/* Prescription */}
                <Box
                  bg="white"
                  border="1px solid #e7edf3"
                  borderRadius="20px"
                  overflow="hidden"
                  boxShadow="0 2px 12px rgba(15,23,42,0.06)"
                >
                  <Flex align="center" justify="space-between" px={5} pt={5} pb={4} borderBottom="1px solid #f1f5f9">
                    <Box>
                      <Text fontWeight="800" color="#0f172a" fontSize="sm" m={0} letterSpacing="-0.01em">Prescription</Text>
                      <Text fontSize="xs" color="#64748b" m={0} mt="2px">
                        {files.length ? `${files.length} file${files.length > 1 ? "s" : ""} attached` : "No prescription attached"}
                      </Text>
                    </Box>
                    <Icon as={FiFileText} color="#0f766e" boxSize={4} />
                  </Flex>

                  <Box px={5} py={4}>
                    {files.length > 0 ? (
                      <Stack spacing={3}>
                        {files.map((f, idx) => {
                          const url = getFileUrl(f);
                          return (
                            <Box key={idx} border="1px solid #e7edf3" borderRadius="14px" overflow="hidden">
                              {url && f.mimeType?.startsWith("image/") && (
                                <Image src={url} alt={f.originalName || `Prescription ${idx + 1}`} w="100%" maxH="220px" objectFit="contain" bg="#f8fafc" />
                              )}
                              <Flex align="center" justify="space-between" px={3} py={2.5} borderTop={url && f.mimeType?.startsWith("image/") ? "1px solid #edf2f7" : undefined}>
                                <HStack spacing={2} minW={0} flex={1}>
                                  <Icon as={f.mimeType?.startsWith("image/") ? FiFileText : FiFile} color="#0f766e" boxSize={3.5} flexShrink={0} />
                                  <Text fontSize="xs" fontWeight="600" color="#0f172a" noOfLines={1} m={0}>
                                    {f.originalName || f.fileName || `File ${idx + 1}`}
                                  </Text>
                                </HStack>
                                {url && (
                                  <Link href={url} isExternal color="#0f766e" fontWeight="700" fontSize="xs" display="inline-flex" alignItems="center" gap={1} flexShrink={0} ml={2}>
                                    Open <Icon as={FiExternalLink} boxSize={3} />
                                  </Link>
                                )}
                              </Flex>
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : (
                      <VStack py={6} spacing={2} color="#94a3b8" border="1.5px dashed #e2e8f0" borderRadius="14px">
                        <Icon as={FiFileText} boxSize={7} />
                        <Text fontSize="sm" m={0} fontWeight="600">No prescription attached</Text>
                      </VStack>
                    )}
                  </Box>
                </Box>

                {/* Delivery route */}
                <Box
                  bg="white"
                  border="1px solid #e7edf3"
                  borderRadius="20px"
                  overflow="hidden"
                  boxShadow="0 2px 12px rgba(15,23,42,0.06)"
                  display="flex"
                  flexDirection="column"
                >
                  <Box px={5} pt={5} pb={3} flexShrink={0}>
                    <Text fontWeight="800" color="#0f172a" fontSize="sm" m={0} letterSpacing="-0.01em" mb={3}>Delivery route</Text>
                    <Stack spacing={2.5}>
                      <Flex align="flex-start" gap={2.5}>
                        <Box w="7px" h="7px" borderRadius="full" bg="#0f766e" mt="5px" flexShrink={0} />
                        <Box>
                          <Text fontSize="10px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" m={0}>From pharmacy</Text>
                          <Text fontSize="xs" fontWeight="700" color="#0f172a" m={0} mt="1px" noOfLines={2}>{pharmacyName}</Text>
                        </Box>
                      </Flex>
                      <Box w="1px" h="14px" bg="#e2e8f0" ml="3px" />
                      <Flex align="flex-start" gap={2.5}>
                        <Box w="7px" h="7px" borderRadius="full" bg="#2563eb" mt="5px" flexShrink={0} />
                        <Box>
                          <Text fontSize="10px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" m={0}>To your door</Text>
                          <Text fontSize="xs" fontWeight="700" color="#0f172a" m={0} mt="1px" noOfLines={2}>{deliveryAddress}</Text>
                        </Box>
                      </Flex>
                    </Stack>
                    <SimpleGrid columns={2} spacing={2} mt={3}>
                      <Box bg="#f8fafc" border="1px solid #edf2f7" borderRadius="10px" px={3} py={2.5}>
                        <Text fontSize="10px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" m={0}>Method</Text>
                        <Text fontSize="xs" fontWeight="800" color="#0f172a" m={0} mt={1}>{selectedDeliveryOption?.label || "Same-day"}</Text>
                      </Box>
                      <Box bg="#f8fafc" border="1px solid #edf2f7" borderRadius="10px" px={3} py={2.5}>
                        <Text fontSize="10px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" m={0}>ETA</Text>
                        <Text fontSize="xs" fontWeight="800" color="#0f172a" m={0} mt={1}>{deliveryEta || "~2-4 hrs"}</Text>
                      </Box>
                    </SimpleGrid>
                    {borzoStatusLabel && (
                      <Box mt={3} px={3} py={2} bg="#ecfdf5" border="1px solid #a7f3d0" borderRadius="8px">
                        <Text fontSize="10px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" m={0}>Live status</Text>
                        <Text fontSize="xs" fontWeight="800" color="#0f766e" m={0} mt={1}>{borzoStatusLabel}</Text>
                        {courierName && (
                          <Text fontSize="11px" color="#475569" m={0} mt={1}>
                            Courier: {courierName}{courierPhone ? ` · ${courierPhone}` : ""}
                          </Text>
                        )}
                      </Box>
                    )}
                    {trackingUrl && (
                      <Link href={trackingUrl} isExternal color="#0f766e" fontWeight="700" fontSize="xs" display="inline-flex" alignItems="center" gap={1} mt={3}>
                        Track live <Icon as={FiExternalLink} boxSize={3} />
                      </Link>
                    )}
                  </Box>
                  <Box flex={1} minH="180px" borderTop="1px solid #f1f5f9" position="relative">
                    <Box position="absolute" top={0} right={0} bottom={0} left={0}>
                      <MapPreviewInline height="100%" borderRadius="0" deliveryCoords={deliveryCoords} pharmacyCoords={pharmacyCoords} showRoute />
                    </Box>
                  </Box>
                </Box>
              </SimpleGrid>
            </Stack>

            {/* ── Right col: payment widget ── */}
            <Box position={{ xl: "sticky" }} top={{ xl: "110px" }}>
              <Box
                bg="white"
                border="1px solid #e7edf3"
                borderRadius="24px"
                overflow="hidden"
                boxShadow="0 8px 32px rgba(15,23,42,0.10)"
              >
                {/* Widget header */}
                <Box
                  bg={paymentDone
                    ? "linear-gradient(135deg, #f0fdf9 0%, #dcfce7 100%)"
                    : requiresPayment
                    ? "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)"
                    : "#f8fafc"}
                  px={5}
                  pt={5}
                  pb={4}
                  borderBottom="1px solid #f1f5f9"
                >
                  <Flex align="center" justify="space-between" mb={1}>
                    <Text fontWeight="900" color="#0f172a" fontSize="lg" m={0} letterSpacing="-0.03em">
                      {paymentDone ? "Payment confirmed" : requiresPayment ? "Complete payment" : "Payment status"}
                    </Text>
                    <Icon
                      as={paymentDone ? FiCheckCircle : FiShield}
                      color={paymentDone ? "#16a34a" : "#92400e"}
                      boxSize={5}
                    />
                  </Flex>
                  <Text fontSize="xs" color="#64748b" m={0} lineHeight="1.5">
                    {paymentDone
                      ? "The pharmacy will now process and dispatch your order."
                      : requiresPayment
                      ? "Review the amount and tap Pay to confirm your order."
                      : "Waiting for pharmacy to confirm the final price."}
                  </Text>
                </Box>

                <Box px={5} py={4}>
                  <Stack spacing={3}>
                    {/* Bill breakdown */}
                    <Box bg="#f8fafc" border="1px solid #edf2f7" borderRadius="16px" px={4} py={3}>
                      <Stack spacing={2.5}>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="sm" color="#64748b" m={0}>Medicines</Text>
                          <Box textAlign="right">
                            {(isPendingUserReview || order?.proposedChanges?.length > 0) && liveTotal !== medicinesSubtotal && (
                              <Text fontSize="xs" color="#94a3b8" textDecoration="line-through" m={0}>₹{medicinesSubtotal.toFixed(2)}</Text>
                            )}
                            <Text fontSize="sm" fontWeight="700" color="#0f172a" m={0}>
                              ₹{((isPendingUserReview || order?.proposedChanges?.length > 0)
                                ? (grossDiscountPercent > 0 ? liveTotal / (1 - grossDiscountPercent / 100) : liveTotal)
                                : (payableTotal - Number(deliveryFee || 0))
                              ).toFixed(2)}
                            </Text>
                          </Box>
                        </Flex>
                        {grossDiscountPercent > 0 && (
                          <Flex justify="space-between" align="center">
                            <Text fontSize="sm" color="#dc2626" m={0}>Overall discount ({grossDiscountPercent}%)</Text>
                            <Text fontSize="sm" fontWeight="700" color="#dc2626" m={0}>
                              −₹{((isPendingUserReview || order?.proposedChanges?.length > 0)
                                ? (liveTotal / (1 - grossDiscountPercent / 100) - liveTotal)
                                : ((payableTotal - Number(deliveryFee || 0)) / (1 - grossDiscountPercent / 100) - (payableTotal - Number(deliveryFee || 0)))
                              ).toFixed(2)}
                            </Text>
                          </Flex>
                        )}
                        <Flex justify="space-between" align="center">
                          <Text fontSize="sm" color="#64748b" m={0}>Delivery</Text>
                          <Text fontSize="sm" fontWeight="700" color="#0f172a" m={0}>{deliveryFee ? `₹${Number(deliveryFee).toFixed(2)}` : "Free"}</Text>
                        </Flex>
                        <Divider borderColor="#edf2f7" />
                        <Flex justify="space-between" align="center">
                          <Text fontSize="sm" fontWeight="800" color="#0f172a" m={0}>Total</Text>
                          <Text fontSize="xl" fontWeight="900" color="#0f172a" m={0} letterSpacing="-0.03em">
                            ₹{((isPendingUserReview || order?.proposedChanges?.length > 0) ? liveTotal + Number(deliveryFee || 0) : payableTotal).toFixed(2)}
                          </Text>
                        </Flex>
                      </Stack>
                    </Box>

                    {/* Pharmacy + delivery info */}
                    <Stack spacing={2}>
                      <Flex align="center" gap={2.5} bg="#f8fafc" border="1px solid #edf2f7" borderRadius="12px" px={3.5} py={2.5}>
                        <Icon as={FiMapPin} color="#0f766e" boxSize={3.5} flexShrink={0} />
                        <Box minW={0}>
                          <Text fontSize="10px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" m={0}>Pharmacy</Text>
                          <Text fontSize="xs" fontWeight="700" color="#0f172a" m={0} noOfLines={1}>{pharmacyName}</Text>
                        </Box>
                      </Flex>
                      <Flex align="center" gap={2.5} bg="#f8fafc" border="1px solid #edf2f7" borderRadius="12px" px={3.5} py={2.5}>
                        <Icon as={FiTruck} color="#0f766e" boxSize={3.5} flexShrink={0} />
                        <Box minW={0}>
                          <Text fontSize="10px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" m={0}>Deliver to</Text>
                          <Text fontSize="xs" fontWeight="700" color="#0f172a" m={0} noOfLines={2}>{deliveryAddress}</Text>
                        </Box>
                      </Flex>
                    </Stack>

                    {/* Payment state */}
                    {paymentDone ? (
                      <Box border="1px solid #bbf7d0" borderRadius="14px" p={3.5} bg="#f0fdf4">
                        <HStack spacing={2}>
                          <Icon as={FiCheckCircle} color="#16a34a" boxSize={4} />
                          <Box>
                            <Text fontWeight="800" color="#166534" m={0} fontSize="sm">Payment received</Text>
                            <Text fontSize="xs" color="#166534" m={0}>
                              {order?.paymentWorkflow?.gatewayPaymentId || "Confirmed via Razorpay"}
                            </Text>
                          </Box>
                        </HStack>
                      </Box>
                    ) : (
                      <>
                        {pendingPricingSync && (
                          <Box border="1px solid #fde68a" borderRadius="12px" p={3.5} bg="#fffbeb">
                            <Text fontWeight="800" color="#92400e" m={0} fontSize="sm" mb={1}>Waiting for final total</Text>
                            <Text fontSize="xs" color="#92400e" m={0} lineHeight="1.5">
                              Pharmacy is confirming availability. Payment will unlock once the total is ready.
                            </Text>
                          </Box>
                        )}

                        <Button
                          w="full"
                          h="52px"
                          bg={requiresPayment ? "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)" : "#f1f5f9"}
                          color={requiresPayment ? "white" : "#94a3b8"}
                          _hover={{
                            bg: requiresPayment ? "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)" : "#f1f5f9",
                            transform: requiresPayment ? "translateY(-1px)" : "none",
                            boxShadow: requiresPayment ? "0 8px 24px rgba(15,118,110,0.38)" : "none",
                          }}
                          _active={{ transform: "none", boxShadow: "none" }}
                          leftIcon={<FiCreditCard size={16} />}
                          onClick={handlePayNow}
                          isLoading={paying}
                          isDisabled={!order?._id || !razorpayKeyId || !razorpayReady || !requiresPayment || isRejected}
                          borderRadius="14px"
                          fontWeight="800"
                          fontSize="md"
                          boxShadow={requiresPayment ? "0 4px 16px rgba(15,118,110,0.28)" : "none"}
                          transition="all 0.2s ease"
                        >
                          {requiresPayment ? `Pay ₹${((isPendingUserReview || order?.proposedChanges?.length > 0) ? liveTotal + Number(deliveryFee || 0) : payableTotal).toFixed(2)} with Razorpay` : "Payment unavailable"}
                        </Button>

                        <Flex align="center" justify="center" gap={1.5}>
                          <Icon as={FiShield} color="#94a3b8" boxSize={3} />
                          <Text fontSize="11px" color="#94a3b8" m={0}>Secured by Razorpay · 256-bit SSL</Text>
                        </Flex>
                      </>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Box>

          </Box>
        </Box>
      </Box>
  );
};

export default PrescriptionOrderSummary;
